import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { useLang } from "../i18n/LanguageContext";
import LanguageToggle from "../components/LanguageToggle";
import BookingsTab from "../components/BookingsTab";
import VideosSection from "../components/VideosSection";
import DevelopmentSection from "../components/DevelopmentSection";
import DevelopmentTimeline from "../components/DevelopmentTimeline";
import SkillsTracking from "../components/SkillsTracking";
import PremiumLock from "../components/PremiumLock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = ["my_profile", "team_calendar", "development", "skills", "videos", "private_session", "my_bookings"];

export default function ParentPortalPage() {
  const { token } = useParams();
  const { t, lang } = useLang();

  const [player, setPlayer] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("my_profile");
  const [trainings, setTrainings] = useState([]);
  const [requests, setRequests] = useState([]);
  const [access, setAccess] = useState({ is_premium: false, pending_bookings_count: 0 });
  const [videos, setVideos] = useState([]);

  const loadAll = async () => {
    try {
      const [p, tr, rq, ac] = await Promise.all([
        axios.get(`${API}/p/${token}`),
        axios.get(`${API}/p/${token}/trainings`),
        axios.get(`${API}/p/${token}/private-requests`),
        axios.get(`${API}/p/${token}/access-status`),
      ]);
      setPlayer(p.data);
      setTrainings(tr.data);
      setRequests(rq.data);
      setAccess(ac.data);
      // Videos list is premium-gated on backend; if unpaid it returns [].
      const v = await axios.get(`${API}/p/${token}/videos`);
      setVideos(v.data);
    } catch {
      setError(t("invalid_link"));
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#080808] text-white flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-6xl mb-3">🔒</div>
          <h1 className="font-bebas text-3xl tracking-widest text-[#e8559a]">
            {error}
          </h1>
        </div>
      </div>
    );
  }
  if (!player) {
    return <div className="min-h-screen bg-[#080808] text-neutral-500 p-10">...</div>;
  }

  const rsvp = async (trId, status) => {
    await axios.post(`${API}/p/${token}/trainings/${trId}/rsvp`, { status });
    setTrainings((arr) =>
      arr.map((tr) => (tr.id === trId ? { ...tr, my_rsvp: status } : tr))
    );
  };

  const submitRequest = async (date, time, notes) => {
    await axios.post(`${API}/p/${token}/private-requests`, { date, time, notes });
    const r = await axios.get(`${API}/p/${token}/private-requests`);
    setRequests(r.data);
  };

  const positionLabel = lang === "es" ? t(`pos_${player.position}`) : player.position;

  return (
    <div className="min-h-screen bg-[#080808] text-white">
      {/* Top */}
      <header className="sticky top-0 z-40 border-b border-[#1a1a1a] bg-[#080808]/85 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-[#e8559a] flex items-center justify-center text-xl shadow-lg shadow-[#e8559a]/30">
              ⚽
            </div>
            <div className="leading-tight">
              <div className="font-bebas text-lg tracking-[0.18em] text-white">SOCCER TROOP</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#e8559a]/80">
                {t("parent_portal")}
              </div>
            </div>
          </div>
          <LanguageToggle />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {/* Hero */}
        <div className="rounded-2xl bg-gradient-to-br from-[#161616] to-[#1a0e16] border border-[#e8559a]/30 p-5 sm:p-6 mb-5">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-20 h-20 rounded-xl bg-[#e8559a]/10 border border-[#e8559a]/40 flex items-center justify-center">
              <span className="font-bebas text-4xl text-[#e8559a] tracking-widest leading-none">
                {player.player_number}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#e8559a]/80 mb-1">
                {t("welcome_back")}, {player.parent_full_name}
              </div>
              <h1 className="font-bebas text-3xl sm:text-4xl tracking-wider leading-tight truncate">
                {player.first_name} {player.last_name}
              </h1>
              <div className="mt-1.5 text-xs text-neutral-400">
                ⚽ {positionLabel} · 🎂 {player.age} {lang === "es" ? "años" : "yrs"}
              </div>
            </div>
          </div>
          <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-neutral-500 flex items-center gap-1.5">
            🔒 {t("readonly_notice")}
          </p>
        </div>

        {/* Premium status banner */}
        <div
          data-testid="portal-premium-banner"
          className={`mb-4 rounded-xl px-4 py-2.5 text-xs uppercase tracking-[0.2em] flex items-center gap-2 ${
            access.is_premium
              ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300"
              : "bg-amber-500/10 border border-amber-500/30 text-amber-300"
          }`}
        >
          {access.is_premium ? "★" : "🔒"}{" "}
          {access.is_premium
            ? t("premium_unlocked")
            : t("premium_locked_title")}
        </div>

        {/* Tabs */}
        <div className="flex gap-1.5 mb-5 border-b border-[#252525] overflow-x-auto">
          {TABS.map((k) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`portal-tab-${k}`}
              className={`px-4 py-2.5 -mb-px text-sm font-medium whitespace-nowrap transition-all border-b-2 ${
                tab === k
                  ? "text-[#e8559a] border-[#e8559a]"
                  : "text-neutral-400 border-transparent hover:text-white"
              }`}
            >
              {k === "my_profile" && "📋 "}
              {k === "team_calendar" && "📅 "}
              {k === "development" && "💡 "}
              {k === "skills" && "📊 "}
              {k === "videos" && "🎬 "}
              {k === "private_session" && "⚡ "}
              {k === "my_bookings" && "💳 "}
              {!access.is_premium && (k === "development" || k === "videos" || k === "skills") && "🔒 "}
              {k === "development" ? t("development") : k === "skills" ? t("skills") : t(k === "videos" ? "tab_videos" : k)}
            </button>
          ))}
        </div>

        {tab === "my_profile" && <ProfileView player={player} t={t} lang={lang} />}
        {tab === "team_calendar" && (
          <TeamCalendarView trainings={trainings} onRsvp={rsvp} t={t} />
        )}
        {tab === "development" &&
          (access.is_premium ? (
            <div className="space-y-6">
              <DevelopmentTimeline token={token} />
              <DevelopmentSection
                mode="parent"
                token={token}
                fileUrlFor={(vid) => `${API}/p/${token}/videos/${vid}/file`}
                videos={videos}
              />
            </div>
          ) : (
            <PremiumLock
              onGoToBookings={() => setTab("my_bookings")}
              hasPendingBooking={access.pending_bookings_count > 0}
            />
          ))}
        {tab === "skills" &&
          (access.is_premium ? (
            <SkillsTracking mode="parent" token={token} />
          ) : (
            <PremiumLock
              onGoToBookings={() => setTab("my_bookings")}
              hasPendingBooking={access.pending_bookings_count > 0}
            />
          ))}
        {tab === "videos" &&
          (access.is_premium ? (
            <VideosSection
              mode="parent"
              playerId={token}
              apiPrefix={`/p/${token}`}
              fileUrlFor={(vid) => `${API}/p/${token}/videos/${vid}/file`}
            />
          ) : (
            <PremiumLock
              onGoToBookings={() => setTab("my_bookings")}
              hasPendingBooking={access.pending_bookings_count > 0}
            />
          ))}
        {tab === "private_session" && (
          <PrivateSessionView requests={requests} onSubmit={submitRequest} t={t} />
        )}
        {tab === "my_bookings" && <BookingsTab token={token} />}
      </main>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-[#252525] last:border-b-0 gap-3">
      <span className="text-xs uppercase tracking-wider text-neutral-500 flex-shrink-0">{label}</span>
      <span className="text-sm text-white text-right truncate">{value || "—"}</span>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-2xl bg-[#161616] border border-[#252525] p-5 sm:p-6">
      <h3 className="font-bebas text-xl tracking-widest text-white mb-3 pb-3 border-b border-[#252525]">
        {title}
      </h3>
      {children}
    </div>
  );
}

function ProfileView({ player, t, lang }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="portal-profile-view">
      <Card title={`📋 ${t("section_player")}`}>
        <Row label={t("first_name")} value={player.first_name} />
        <Row label={t("last_name")} value={player.last_name} />
        <Row label={t("date_of_birth")} value={player.date_of_birth} />
        <Row label={t("age")} value={player.age} />
        <Row label={t("primary_position")} value={lang === "es" ? t(`pos_${player.position}`) : player.position} />
        <Row label={t("dominant_foot")} value={lang === "es" ? t(`foot_${player.dominant_foot}`) : player.dominant_foot} />
        <Row label={t("experience")} value={lang === "es" ? t(`exp_${player.experience}`) : player.experience} />
        <Row label={t("current_club")} value={player.current_club} />
      </Card>
      <Card title={`👨‍👩‍👧 ${t("section_parent")}`}>
        <Row label={t("parent_full_name")} value={player.parent_full_name} />
        <Row label={t("email")} value={player.email} />
        <Row label={t("phone")} value={player.phone} />
        <Row label={t("emergency_contact")} value={`${player.emergency_contact?.name || ""} · ${player.emergency_contact?.phone || ""}`} />
      </Card>
      <Card title={`🏥 ${t("medical")}`}>
        <Row label={t("has_medical")} value={player.has_medical_condition ? t("yes") : t("no")} />
        {player.has_medical_condition && (
          <Row label={t("medical_details")} value={player.medical_condition_details} />
        )}
        <Row label={t("allergies")} value={player.allergies} />
        <Row label={t("medications")} value={player.medications} />
      </Card>
      <Card title={`✅ ${t("consent_status")}`}>
        {[
          ["marketing", "consent_marketing_title"],
          ["social_media", "consent_social_title"],
          ["insurance_waiver", "consent_insurance_title"],
          ["liability_waiver", "consent_liability_title"],
          ["general", "consent_general_title"],
        ].map(([k, labelKey]) => (
          <Row
            key={k}
            label={t(labelKey)}
            value={player.consent?.[k] ? <span className="text-emerald-400">✓ {t("accepted")}</span> : "—"}
          />
        ))}
      </Card>
    </div>
  );
}

function TeamCalendarView({ trainings, onRsvp, t }) {
  const todayStr = new Date().toISOString().slice(0, 10);
  const upcoming = trainings.filter((tr) => tr.date >= todayStr);
  const past = trainings.filter((tr) => tr.date < todayStr).slice(0, 20);

  if (trainings.length === 0) {
    return (
      <div className="rounded-2xl bg-[#161616] border border-[#252525] p-10 text-center text-neutral-500">
        {t("no_trainings")}
      </div>
    );
  }

  const RSVP_OPTS = [
    { v: "yes", icon: "✅", label: "rsvp_yes", color: "bg-emerald-500/15 border-emerald-500/40 text-emerald-400" },
    { v: "maybe", icon: "🤔", label: "rsvp_maybe", color: "bg-amber-500/15 border-amber-500/40 text-amber-400" },
    { v: "no", icon: "❌", label: "rsvp_no", color: "bg-[#e05555]/15 border-[#e05555]/40 text-[#e05555]" },
  ];

  const TrainingCard = ({ tr, isPast }) => (
    <div
      data-testid={`portal-training-${tr.id}`}
      className="rounded-xl bg-[#161616] border border-[#252525] p-4"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-14 text-center rounded-lg bg-[#0f0f0f] border border-[#252525] py-2">
          <div className="font-bebas text-2xl text-[#e8559a] tracking-widest leading-none">
            {(tr.date || "").slice(8, 10)}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">
            {(tr.date || "").slice(5, 7)}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-white">{tr.title}</div>
          <div className="text-xs text-neutral-400 mt-1">
            🕒 {tr.time} · 📍 {tr.location || "—"} · ⏱ {tr.duration_minutes} min
          </div>
          {tr.description && (
            <p className="text-xs text-neutral-500 mt-2 line-clamp-2">{tr.description}</p>
          )}
        </div>
      </div>
      {!isPast && (
        <div className="mt-3 grid grid-cols-3 gap-1.5">
          {RSVP_OPTS.map((o) => {
            const active = tr.my_rsvp === o.v;
            return (
              <button
                key={o.v}
                type="button"
                onClick={() => onRsvp(tr.id, o.v)}
                data-testid={`rsvp-${tr.id}-${o.v}`}
                className={`px-2 py-2 rounded-lg border text-xs font-medium transition-all ${
                  active
                    ? o.color + " ring-2 ring-current/40"
                    : "bg-[#0f0f0f] border-[#252525] text-neutral-400 hover:border-[#e8559a]/40"
                }`}
              >
                {o.icon} {t(o.label)}
              </button>
            );
          })}
        </div>
      )}
      {isPast && tr.my_attendance && (
        <div className="mt-3 inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full border border-[#252525] text-neutral-300">
          {tr.my_attendance === "present" && "✅"}
          {tr.my_attendance === "absent" && "❌"}
          {tr.my_attendance === "late" && "🕒"}
          {tr.my_attendance === "excused" && "📋"}
          <span className="capitalize">{t(tr.my_attendance)}</span>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-5" data-testid="portal-team-calendar">
      <div>
        <h3 className="font-bebas text-2xl tracking-widest text-white mb-3">
          📅 {t("upcoming_trainings")}
        </h3>
        {upcoming.length === 0 ? (
          <div className="rounded-xl bg-[#161616] border border-[#252525] p-6 text-center text-neutral-500 text-sm">
            {t("no_trainings")}
          </div>
        ) : (
          <div className="space-y-2.5">
            {upcoming.map((tr) => <TrainingCard key={tr.id} tr={tr} />)}
          </div>
        )}
      </div>
      {past.length > 0 && (
        <div>
          <h3 className="font-bebas text-2xl tracking-widest text-neutral-400 mb-3">
            📜 {t("past_trainings")}
          </h3>
          <div className="space-y-2.5 opacity-80">
            {past.map((tr) => <TrainingCard key={tr.id} tr={tr} isPast />)}
          </div>
        </div>
      )}
    </div>
  );
}

function PrivateSessionView({ requests, onSubmit, t }) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("18:00");
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!date) return;
    setSending(true);
    try {
      await onSubmit(date, time, notes);
      setDate(""); setTime("18:00"); setNotes("");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full bg-[#0f0f0f] border border-[#252525] rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#e8559a] focus:outline-none";

  const statusBadge = (s) => {
    if (s === "approved") return <span className="px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 text-xs">✓ {t("approved")}</span>;
    if (s === "declined") return <span className="px-2.5 py-1 rounded-full bg-[#e05555]/15 border border-[#e05555]/40 text-[#e05555] text-xs">✕ {t("declined")}</span>;
    return <span className="px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-400 text-xs">⧗ {t("pending")}</span>;
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-5" data-testid="portal-private-session">
      <Card title={`⚡ ${t("request_private")}`}>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1.5">{t("training_date")}</label>
              <input
                type="date"
                data-testid="pr-date"
                className={inputCls}
                value={date}
                min={today}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1.5">{t("training_time")}</label>
              <input
                type="time"
                data-testid="pr-time"
                className={inputCls}
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1.5">{t("request_notes")}</label>
            <textarea
              rows={3}
              data-testid="pr-notes"
              className={`${inputCls} resize-none`}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Trabajar tiro de media distancia..."
            />
          </div>
          <button
            type="submit"
            disabled={sending || !date}
            data-testid="pr-submit"
            className="w-full px-5 py-3 rounded-lg bg-[#e8559a] hover:bg-[#c9407f] disabled:opacity-50 text-white font-bebas tracking-widest transition-colors"
          >
            {sending ? "..." : `⚡ ${t("request_submit")}`}
          </button>
        </form>
      </Card>

      <Card title={`📋 ${t("my_requests")}`}>
        {requests.length === 0 ? (
          <div className="text-center text-neutral-500 text-sm py-4">{t("no_requests")}</div>
        ) : (
          <div className="space-y-2">
            {requests.map((r) => (
              <div key={r.id} className="rounded-lg bg-[#0f0f0f] border border-[#252525] p-3.5 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-medium">
                    {r.date} · {r.time}
                  </div>
                  {r.notes && <div className="text-xs text-neutral-400 mt-1">{r.notes}</div>}
                  {r.admin_note && (
                    <div className="text-xs text-[#e8559a] mt-2 italic">"{r.admin_note}"</div>
                  )}
                </div>
                {statusBadge(r.status)}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
